// drawolaUsage ledger: our own byte-count, not origin-wide.
//
// Kept in the META store under `META_KEYS.USAGE` ("drawolaUsage" — the single
// definition lives in ./db.ts, shared with multiFileStore.ts, which is the
// writer). Updated inside every save / delete transaction so it stays
// consistent; `repairUsageLedger()` recomputes it from the stored docs and is
// what migration v3 runs to heal browsers written by pre-fix builds.

import type { BinaryFiles } from "@excalidraw/excalidraw/types";
import type { OrderedExcalidrawElement } from "@excalidraw/element/types";

import {
  LEGACY_USAGE_META_KEY,
  META_KEYS,
  STORES,
  promisify,
  withTx,
} from "./db";

import type { DocMeta } from "./types";

export async function getDrawolaUsage(): Promise<number> {
  return withTx([STORES.META], "readonly", async (tx) => {
    const v = await promisify(
      tx.objectStore(STORES.META).get(META_KEYS.USAGE) as IDBRequest<
        number | undefined
      >,
    );
    return v ?? 0;
  });
}

export function byteLengthOfDataURL(dataURL: string): number {
  // Good-enough approximation. We don't decode — just use string length.
  // base64 expands bytes by ~4/3, but using raw string length is a consistent
  // upper-bound and matches what IndexedDB stores (UTF-16 string).
  return dataURL.length;
}

export function estimateDocBytes(
  elements: readonly OrderedExcalidrawElement[],
  appState: unknown,
  files: BinaryFiles,
): number {
  let n = 0;
  try {
    n += JSON.stringify(elements).length;
  } catch {
    // ignore
  }
  try {
    n += JSON.stringify(appState).length;
  } catch {
    // ignore
  }
  for (const id in files) {
    const f = files[id];
    if (f && typeof f.dataURL === "string") {
      n += byteLengthOfDataURL(f.dataURL);
    }
  }
  return n;
}

/**
 * Recompute the ledger from the documents actually on disk, and drop the
 * mis-cased `"drawOlaUsage"` entry left behind by pre-fix builds.
 *
 * Both halves run in ONE readwrite transaction, so a browser can never end up
 * with the legacy key deleted but the correct one unwritten. Idempotent and
 * safe to call on a fresh install (the scan just finds no docs and writes 0),
 * which is why migration v3 runs it unconditionally rather than probing first.
 *
 * @returns the recomputed total in bytes
 */
export async function repairUsageLedger(): Promise<number> {
  return withTx([STORES.DOCS, STORES.META], "readwrite", async (tx) => {
    const docs = (await promisify(
      tx.objectStore(STORES.DOCS).getAll() as IDBRequest<DocMeta[]>,
    )) as DocMeta[];
    const total = docs.reduce((s, d) => s + (d.sizeBytes ?? 0), 0);
    const metaStore = tx.objectStore(STORES.META);
    await promisify(metaStore.put(total, META_KEYS.USAGE));
    await promisify(metaStore.delete(LEGACY_USAGE_META_KEY));
    return total;
  });
}
